import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { signUp } from 'aws-amplify/auth';
import {
  validateEmail,
  validateEmailWithException,
  validateNotEmpty,
  validateOrganization,
} from '../Support/ValidationLogic';
import SharedButton from '@/global/Button/Button';
import SharedDialog from '@/global/Modal/Modal';
import { ArrowPathIcon, ArrowRightIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import useText from '@/hooks/useText';
import VerifyEmailPopup from './VerifyEmailPopUp';
import ResendConfirmationEmailButton from './ResendConfirmationEmailButton';
import MultiSelectDropdown from '@/components/Academic/MultiSelectDropdown';
import SignupSelect from './SignupSelect';
import RorInstitutionSelect from './RorInstitutionSelect';

function scorePassword(pw: string) {
  let score = 0;
  if (pw.length >= 10) score++;
  if (pw.length >= 14) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
}

function SignupField({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="signup-field">
      <div className="signup-field-header">
        <span className="signup-field-label">{label}</span>
        {hint && <span className="signup-field-hint">{hint}</span>}
      </div>
      {children}
      {error && <p className="signup-field-error">{error}</p>}
    </label>
  );
}

const RESEARCH_CONTEXT_OPTIONS = [
  { value: 'DRUG-RD', label: 'Drug R&D' },
  { value: 'CLINICAL-GENOMICS', label: 'Clinical genomics' },
  { value: 'AI-ML-DEVELOPMENT', label: 'AI/ML development' },
  { value: 'BASIC-AND-TRANSLATIONAL-RESEARCH', label: 'Basic and translational research' },
  { value: 'OTHER', label: 'Other' },
] as const;

const ORGANIZATION_TYPE_OPTIONS = [
  { value: 'NON-PROFIT', label: 'University / Institution' },
  { value: 'FOR-PROFIT', label: 'Company' },
];

const PURCHASE_INTENT_OPTIONS = [
  { value: 'BUDGETED', label: 'Budgeted; evaluating options before purchase' },
  { value: 'NO-BUDGET', label: 'No budget; exploring for research purposes only' },
  { value: 'NO-PLANS', label: 'No current purchase plans' },
];

function formatResearchContext(contexts: string[], otherText: string) {
  return contexts.map(context => (context === 'OTHER' ? `OTHER:${otherText.trim()}` : context)).join(';');
}

const CONTACT_API_URL =
  process.env.REACT_APP_CONTACT_API_URL || 'https://5kdsze9xs4.execute-api.eu-central-1.amazonaws.com';

const KNOWN_COGNITO_SIGNUP_ERRORS = new Set([
  'UsernameExistsException',
  'InvalidPasswordException',
  'InvalidParameterException',
  'CodeDeliveryFailureException',
  'NotAuthorizedException',
  'TooManyRequestsException',
  'LimitExceededException',
  'InvalidEmailRoleAccessPolicyException',
  'UnexpectedLambdaException',
  'UserLambdaValidationException',
]);

function isKnownCognitoSignUpError(e: any): boolean {
  const name = e?.name || e?.code;
  return typeof name === 'string' && KNOWN_COGNITO_SIGNUP_ERRORS.has(name);
}

async function syncSignupResearchContext(email: string, researchContext: string) {
  const response = await fetch(`${CONTACT_API_URL}/signup-research-context`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, researchContext }),
  });

  if (!response.ok) {
    throw new Error(`Research context sync failed (${response.status})`);
  }
}

function ConfirmationPopup({
  onClose,
  onConfirm,
  isAcademic,
  isSubmitting,
}: {
  onClose: () => void;
  onConfirm: (evt: any) => Promise<void>;
  isAcademic: boolean;
  isSubmitting: boolean;
}) {
  return (
    <SharedDialog
      isOpen
      onClose={isSubmitting ? () => undefined : onClose}
      title="Confirm your submission"
      size="xs"
      hasConfirmBtn={false}
      hasCancelBtn={false}
      className="!max-w-lg !space-y-5 !rounded-2xl !p-8"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-pink/10">
          <CheckCircleIcon className="h-6 w-6 text-pink" aria-hidden />
        </div>
        <p className="text-sm leading-relaxed text-gray-600">
          {isAcademic
            ? 'Your application will be manually reviewed. Please double-check that all information is complete and accurate. Incomplete or inaccurate information may result in rejection.'
            : 'Please ensure all information is complete and accurate before submitting your free trial application.'}
        </p>
      </div>

      <div className="flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
        <SharedButton
          type="button"
          variant="secondaryOutline"
          onClick={onClose}
          disabled={isSubmitting}
          className="sm:min-w-[10rem] mr-0"
        >
          Review & Edit
        </SharedButton>
        <SharedButton
          type="button"
          variant="gradient"
          onClick={onConfirm}
          disabled={isSubmitting}
          className="sm:min-w-[10rem] mr-0"
          aria-label="submit-modal"
          iconLeft={isSubmitting ? <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden /> : undefined}
        >
          {isSubmitting ? 'Submitting…' : 'Confirm & Submit'}
        </SharedButton>
      </div>
    </SharedDialog>
  );
}

function SignUpForm({ variant = 'legacy' }: { variant?: 'legacy' | 'card' }) {
  const isCardVariant = variant === 'card';
  const isAcademic = location.pathname === '/academic-apply';
  const academicButton = useText('signup.apply_trial.academic_button');
  const advancedButton = useText('signup.apply_trial.advanced_button');
  const startTrialButton = useText('signup.apply_trial.start_button');
  const heroTitle = useText(isAcademic ? 'signup.apply_academic.title' : 'signup.apply_trial.title');
  const heroTitleHighlight = useText(
    isAcademic ? 'signup.apply_academic.title_highlight' : 'signup.apply_trial.title_highlight',
  );
  const heroTitleSuffix = useText(
    isAcademic ? 'signup.apply_academic.title_suffix' : 'signup.apply_trial.title_suffix',
  );

  const [isChina, setIsChina] = useState(false);

  const [formErrorMessage, setFormErrorMessage] = useState(null);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

  const [utmSource, setUtmSource] = useState('direct');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    if (source) {
      setUtmSource(source);
      sessionStorage.setItem('signup_source', source);
    }
  }, []);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [organization, setOrganization] = useState('');
  const [fieldOfStudy, setFieldOfStudy] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [profile, setProfile] = useState('');
  const [researchContext, setResearchContext] = useState<string[]>([]);
  const [researchContextOther, setResearchContextOther] = useState('');
  const [purchaseIntent, setPurchaseIntent] = useState('');
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [academicLicenseAccepted, setAcademicLicenseAccepted] = useState(false);

  const [password, setPassword] = useState('');
  const [rpassword, setRpassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isRpasswordVisible, setIsRpasswordVisible] = useState(false);

  const [isNameValid, setIsNameValid] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [isOrganizationValid, setIsOrganizationValid] = useState(true);
  const [isResearchContextOtherValid, setIsResearchContextOtherValid] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isPasswordMatching, setIsPasswordMatching] = useState(true);
  const [isRpasswordValid, setIsRpasswordValid] = useState(true);
  const [showVerifyEmailPopup, setShowVerifyEmailPopup] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [showResendForExistingUser, setShowResendForExistingUser] = useState(false);
  const [submissionStatusUnknown, setSubmissionStatusUnknown] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const emailCheckIdRef = useRef(0);

  const normalizeEmail = (value: string) => value.trim().toLowerCase();
  const passwordScore = useMemo(() => scorePassword(password), [password]);
  const passwordScoreLabel = ['Too weak', 'Weak', 'Okay', 'Strong', 'Excellent'][passwordScore];
  const submitLabel =
    isCardVariant && !isAcademic ? startTrialButton : isAcademic ? academicButton : advancedButton;

  //remove autocomplete and keep css
  useEffect(() => {
    const disableAutoComplete = input => {
      input.setAttribute('readonly', 'readonly');
      setTimeout(() => {
        input.removeAttribute('readonly');
      }, 100);
    };
    const inputs = document.querySelectorAll('input');
    inputs.forEach(input => disableAutoComplete(input));
  }, []);

  // LinkedIn Insight Tag
  useEffect(() => {
    const _linkedin_partner_id = '8934697';
    (window as any)._linkedin_data_partner_ids = (window as any)._linkedin_data_partner_ids || [];
    (window as any)._linkedin_data_partner_ids.push(_linkedin_partner_id);

    (function (l) {
      if (!l) {
        (window as any).lintrk = function (a, b) {
          (window as any).lintrk.q.push([a, b]);
        };
        (window as any).lintrk.q = [];
      }
      const s = document.getElementsByTagName('script')[0];
      if (s && s.parentNode) {
        const b = document.createElement('script');
        b.type = 'text/javascript';
        b.async = true;
        b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
        s.parentNode.insertBefore(b, s);
      }
    })((window as any).lintrk);

    return () => {
      const scripts = document.querySelectorAll(
        'script[src="https://snap.licdn.com/li.lms-analytics/insight.min.js"]',
      );
      scripts.forEach(script => script.remove());
    };
  }, []);

  function resetForm() {
    setName('');
    setEmail('');
    setIsChina(false);
    setPhone('');
    setOrganization('');
    setFieldOfStudy('');
    setLinkedin('');
    setProfile('');
    setResearchContext([]);
    setResearchContextOther('');
    setIsResearchContextOtherValid(true);
    setPurchaseIntent('');
    setPrivacyPolicyAccepted(false);
    setAcademicLicenseAccepted(false);
    setPassword('');
    setRpassword('');
    setIsPasswordVisible(false);
    setIsRpasswordVisible(false);
    setFormErrorMessage(null);
    setIsFormSubmitted(false);
    setSubmissionStatusUnknown(false);
  }

  async function validateForm() {
    if (!privacyPolicyAccepted) {
      setFormErrorMessage('You must accept the privacy policy before submitting.');
      return false;
    }
    if (isAcademic && !academicLicenseAccepted) {
      setFormErrorMessage('You must accept the license agreement before submitting.');
      return false;
    }

    if (!isAcademic && !profile) {
      setFormErrorMessage('Please select a company profile');
      return false;
    }

    if (!isAcademic && !purchaseIntent) {
      setFormErrorMessage('Please select a purchase intent');
      return false;
    }

    if (researchContext.includes('OTHER') && !researchContextOther.trim()) {
      setIsResearchContextOtherValid(false);
      setFormErrorMessage('Please specify your research context');
      return false;
    }
    setIsResearchContextOtherValid(true);

    if (isChina && !isPhoneValid) {
      setFormErrorMessage('Phone number should be +86 and 11 digits');
    }

    const textValidations = [
      validateNameInput(name),
      await validateEmailInput(email),
      validateOrganizationInput(organization),
      validatePasswordsInput(password, rpassword),
    ];
    if (!textValidations.every(bool => bool === true)) {
      setFormErrorMessage('Check text inputs for errors');
      return false;
    }
    setFormErrorMessage('');
    return true;
  }

  const handleNameChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setName(evt.target.value);
  };

  const handleNameBlur = (evt: React.FocusEvent<HTMLInputElement>) => {
    validateNameInput(evt.target.value);
  };

  const nameRegex = /^[\p{L}\p{M}'´` -]+$/u;

  function validateNameInput(value: string) {
    const isValid = validateNotEmpty(value) && nameRegex.test(value);
    setIsNameValid(isValid);
    return isValid;
  }

  const handleEmailChange = evt => {
    const rawValue = evt.target.value || '';
    setEmail(rawValue.trimStart());
    if (rawValue.includes('.cn')) {
      setIsChina(true);
      setPhone(forceCnPhone(phone));
    } else {
      setIsChina(false);
    }
  };

  const handleEmailBlur = evt => {
    void validateEmailInput(evt.target.value);
  };

  async function validateEmailInput(value) {
    const requestId = ++emailCheckIdRef.current;
    const cleaned = normalizeEmail(value);
    const localValid = validateEmail(cleaned, isAcademic);
    if (localValid) {
      setIsEmailValid(true);
      return true;
    }

    setIsEmailValid(false);
    const isValid = await validateEmailWithException(cleaned, isAcademic);
    if (requestId !== emailCheckIdRef.current) return isValid;
    setIsEmailValid(isValid);
    return isValid;
  }

  // Phone input
  const forceCnPhone = value => {
    if (!value.startsWith('+86 ')) {
      if (value === '+86') {
        value = '+86 ';
      } else {
        value = '+86 ' + value;
      }
    }
    return value;
  };

  const handlePhoneChange = evt => {
    // only configured for chinese phone numbers
    let val = evt.target.value;
    if (isChina) {
      val = forceCnPhone(val);
    }
    setPhone(val);
  };

  const handlePhoneBlur = evt => {
    const isValid = validatePhoneInput(evt.target.value);
  };

  function validatePhoneInput(value) {
    const isValid = validateNotEmpty(value) && value.length === 15;
    setIsPhoneValid(isValid);
    return isValid;
  }

  // organization input
  const handleOrganizationChange = (valueOrEvent: string | { target: { value: string } }) => {
    const next = typeof valueOrEvent === 'string' ? valueOrEvent : (valueOrEvent?.target?.value ?? '');
    setOrganization(next);
  };

  const handleOrganizationBlur = (evt?: { target?: { value?: string } }) => {
    validateOrganizationInput(evt?.target?.value ?? organization);
  };

  function validateOrganizationInput(value) {
    const isValid = validateOrganization(value);
    setIsOrganizationValid(isValid);
    return isValid;
  }

  // Field of study input
  const handlefieldOfStudyChange = evt => {
    setFieldOfStudy(evt.target.value);
  };

  // linkedin input
  const handlesupportingLinksChange = evt => {
    setLinkedin(evt.target.value);
  };

  // organization type input
  const handleProfileChange = (value: string) => {
    setProfile(value);
  };

  const handleResearchContextChange = (value: string[]) => {
    setResearchContext(value);
    if (!value.includes('OTHER')) {
      setResearchContextOther('');
      setIsResearchContextOtherValid(true);
    } else if (!researchContextOther.trim()) {
      setIsResearchContextOtherValid(false);
    }
  };

  const handleResearchContextOtherChange = (value: string) => {
    setResearchContextOther(value);
    if (value.trim()) {
      setIsResearchContextOtherValid(true);
    }
  };

  const handleResearchContextOtherBlur = () => {
    if (researchContext.includes('OTHER')) {
      setIsResearchContextOtherValid(!!researchContextOther.trim());
    }
  };

  // purchaseIntent input
  const handlePurchaseIntentChange = (value: string) => {
    setPurchaseIntent(value);
  };

  // checkboxes
  const handleAcademicLicenseCheckboxChange = evt => {
    setAcademicLicenseAccepted(evt.target.checked);
  };

  const handlePrivacyPolicyCheckboxChange = evt => {
    setPrivacyPolicyAccepted(evt.target.checked);
  };

  // password/rpassword input
  const handlePasswordChange = evt => {
    setPassword(evt.target.value);
  };

  const handlePasswordBlur = evt => {
    validatePasswordsInput(evt.target.value, rpassword);
  };

  const handleRpasswordChange = evt => {
    setRpassword(evt.target.value);
  };

  const handleRpasswordBlur = evt => {
    validatePasswordsInput(password, evt.target.value);
    // check rpassword for empty
    const isValidR = validateNotEmpty(rpassword);
    setIsRpasswordValid(isValidR);
  };

  function validatePasswordsInput(password, rpassword) {
    // check password for empty
    const isValid = validateNotEmpty(password);
    setIsPasswordValid(isValid);

    // check if passwords match
    let doPasswordsMatch;
    if (password && rpassword) {
      doPasswordsMatch = password === rpassword;
      setIsPasswordMatching(doPasswordsMatch);
    }
    return isValid && doPasswordsMatch;
  }

  const handleOpenConfirmationPopup = async () => {
    if (await validateForm()) {
      setShowConfirmationPopup(true);
    }
  };

  async function handleSubmit(evt) {
    evt.preventDefault();
    if (submitInFlightRef.current || isSubmitting) {
      return;
    }
    submitInFlightRef.current = true;

    const created_at = new Date().getTime().toString();
    const normalizedEmail = normalizeEmail(email);

    const source = sessionStorage.getItem('signup_source') || utmSource;
    const researchContextValue = formatResearchContext(researchContext, researchContextOther);

    const payload = {
      username: normalizedEmail,
      password: password,
      options: {
        userAttributes: {
          email: normalizedEmail,
          phone_number: phone.replace(/\s+/g, ''),
          'custom:company': organization,
          'custom:name': name,
          'custom:created_at': created_at,
          'custom:linkedin': linkedin,
          'custom:field_of_study': fieldOfStudy,
          profile,
          'custom:purchase_intent': purchaseIntent,
          'custom:application_role': isAcademic ? 'ROLE_ACADEMIC' : 'ROLE_TRIAL',
          'custom:source': source,
        },
      },
    };

    if (!(await validateForm())) {
      submitInFlightRef.current = false;
      return;
    }

    setFormErrorMessage('Sending...');
    setSubmissionStatusUnknown(false);
    setIsSubmitting(true);
    try {
      await signUp(payload);

      if (researchContextValue) {
        try {
          await syncSignupResearchContext(normalizedEmail, researchContextValue);
        } catch (syncError) {
          console.error('Failed to sync research context to ActiveCampaign:', syncError);
        }
      }

      setRegisteredEmail(normalizedEmail);
      resetForm();
      setShowVerifyEmailPopup(true);
      setIsFormSubmitted(true);
      setShowResendForExistingUser(false);
      setShowConfirmationPopup(false);
      sessionStorage.removeItem('signup_source');
    } catch (e) {
      const usernameExists = e?.name === 'UsernameExistsException' || e?.code === 'UsernameExistsException';
      if (usernameExists) {
        setRegisteredEmail(normalizedEmail);
        setShowResendForExistingUser(true);
        setSubmissionStatusUnknown(false);
        setFormErrorMessage(
          'This email is already registered. If you have not confirmed your account yet, please check your inbox or resend the confirmation email below.',
        );
      } else if (isKnownCognitoSignUpError(e)) {
        setShowResendForExistingUser(false);
        setSubmissionStatusUnknown(false);
        setFormErrorMessage(
          e?.message ||
            'There was an error with application submission. Please try again or contact us by email at info@disgenet.com',
        );
      } else {
        setRegisteredEmail(normalizedEmail);
        setShowResendForExistingUser(false);
        setSubmissionStatusUnknown(true);
        setFormErrorMessage(
          "We couldn't confirm whether your application went through due to a connection issue. Please check your email for a confirmation message before retrying — if you already received one, there's no need to submit again. Otherwise, use the button below to resend it.",
        );
      }
    } finally {
      setIsSubmitting(false);
      submitInFlightRef.current = false;
    }
  }

  const cardInputClass = (invalid = false) =>
    `signup-form-input${invalid ? ' signup-form-input--invalid' : ''}`;

  const legacyForm = (
    <div className="form-apply w-full max-w-2xl rounded-2xl">
      <h6 className="text-center mb-4 text-xl font-semibold flex items-center justify-center gap-2">
        Sign up form
        <img src="/static/images/icons/user-pen-lucide.svg" alt="User Pen" className="w-6 h-6" />
      </h6>
      <form onSubmit={handleSubmit}>
        {/* Full name and email */}
        <div className="flex mb-4 gap-2">
          <div className="text-left form-q">
            <label htmlFor="name" className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
              full name *
            </label>
            <input
              className={`form-input ${!isNameValid ? 'invalid-input' : ''}`}
              aria-label="name"
              id="name"
              type="text"
              placeholder="Full Name..."
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              value={name}
              data-validate="Name is required"
            />
            <div>{!isNameValid && <p className="apply-input-error">Please enter a valid name</p>}</div>
          </div>
          <div className="text-left form-q">
            <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase" htmlFor="email">
              email *
            </label>
            <input
              className="form-input"
              aria-label="email"
              type="text"
              id="email"
              placeholder="Email address..."
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              value={email}
              data-validate="Valid email is required: ex@abc.xyz"
            />
            {!isEmailValid && (
              <p className="apply-input-error">
                {isAcademic
                  ? 'Please enter a valid institutional email'
                  : 'Only academic or business emails are accepted'}
              </p>
            )}
          </div>
        </div>
        {isChina && (
          <div className="text-left form-q">
            <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">Phone number *</label>
            <div className="line-input" data-validate="Valid phone number is required">
              <input
                className="form-input"
                aria-label="phone"
                type="text"
                placeholder="Phone number..."
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                value={phone}
              />
            </div>
            <div>
              {!isPhoneValid && (
                <p className="apply-input-error ">
                  Please enter a valid phone number with no special characters
                </p>
              )}
            </div>
            <p>
              Our official distributor, <a href="cloudscientific.com">Cloudscientific</a>, is here to assist
              you. Contact them at: +86 02154975000
            </p>
          </div>
        )}
        <div className="flex mb-4 gap-2">
          <div className="text-left  form-q">
            <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">Organization *</label>
            <div data-validate="Name is required">
              {isAcademic ? (
                <RorInstitutionSelect
                  value={organization}
                  onChange={handleOrganizationChange}
                  onBlur={() => handleOrganizationBlur()}
                  invalid={!isOrganizationValid}
                  variant="form"
                  placeholder="Search university or research institute…"
                />
              ) : (
                <input
                  className="form-input"
                  aria-label="organization"
                  type="text"
                  placeholder="Company..."
                  onChange={handleOrganizationChange}
                  onBlur={handleOrganizationBlur}
                  value={organization}
                />
              )}
            </div>
            <div>
              {!isOrganizationValid && (
                <p className="apply-input-error">Please enter your organization&apos;s name</p>
              )}
            </div>
          </div>
          <div className="text-left form-q">
            <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
              What best describes your research context?
            </label>
            <MultiSelectDropdown
              options={[...RESEARCH_CONTEXT_OPTIONS]}
              value={researchContext}
              onChange={handleResearchContextChange}
              placeholder="Select your research context..."
              variant="form"
              otherOptionValue="OTHER"
              otherValue={researchContextOther}
              onOtherChange={handleResearchContextOtherChange}
              onOtherBlur={handleResearchContextOtherBlur}
              otherError={
                researchContext.includes('OTHER') && !isResearchContextOtherValid
                  ? 'Please specify your research context'
                  : undefined
              }
              otherPlaceholder="Please specify..."
              otherRequired
              otherInputName="research-context-other"
            />
          </div>
        </div>
        <div className="flex mb-4 gap-2">
          {!isAcademic && (
            <>
              <div className="text-left  form-q">
                <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                  Organization Type *
                </label>
                <div>
                  <select
                    className="form-input"
                    aria-label="organizationType"
                    onChange={evt => handleProfileChange(evt.target.value)}
                    value={profile}
                  >
                    <option value="" disabled>
                      Choose one...
                    </option>
                    <option value="NON-PROFIT">University/Institution</option>
                    <option value="FOR-PROFIT">Company</option>
                  </select>
                </div>
              </div>
              <div className="text-left  form-q">
                <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                  Purchase Intent *
                </label>
                <div>
                  <select
                    className="form-input"
                    aria-label="purchaseIntent"
                    onChange={evt => handlePurchaseIntentChange(evt.target.value)}
                    value={purchaseIntent}
                  >
                    <option value="" disabled>
                      Choose one...
                    </option>
                    <option value="BUDGETED">Budgeted; evaluating options before purchase</option>
                    <option value="NO-BUDGET">No budget; exploring for research purposes only</option>
                    <option value="NO-PLANS">No current purchase plans</option>
                  </select>
                </div>
              </div>
            </>
          )}
          {isAcademic && (
            <div className="text-left w-full">
              <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                Field of Study
              </label>
              <div>
                <input
                  className="form-input"
                  aria-label="fieldOfStudy"
                  type="text"
                  placeholder="Bioinformatics, Genomics..."
                  onChange={handlefieldOfStudyChange}
                  value={fieldOfStudy}
                />
              </div>
            </div>
          )}
        </div>
        <div className="flex mb-4 gap-2">
          <div className="text-left form-q">
            <div className="flex justify-between">
              <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase" htmlFor="password">
                Password *
              </label>
              <button
                type="button"
                className="eye-icon"
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
              >
                {isPasswordVisible ? (
                  <EyeIcon className="w-4 h-4 mb-1" />
                ) : (
                  <EyeSlashIcon className="w-4 h-4 mb-1" />
                )}
              </button>
            </div>
            <div data-validate="Password is required">
              <input
                className="form-input"
                aria-label="password"
                id="password"
                type={isPasswordVisible ? 'text' : 'password'}
                placeholder=""
                onChange={handlePasswordChange}
                onBlur={handlePasswordBlur}
                value={password}
              />
            </div>
            <div>{!isPasswordValid && <p className="apply-input-error">Please enter a password</p>}</div>
          </div>
          <div className="text-left form-q">
            <div className="flex justify-between">
              <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase" htmlFor="rpassword">
                Retype Password *
              </label>
              <button
                type="button"
                className="eye-icon"
                onClick={() => setIsRpasswordVisible(!isRpasswordVisible)}
              >
                {isRpasswordVisible ? (
                  <EyeIcon className="w-4 h-4 mb-1" />
                ) : (
                  <EyeSlashIcon className="w-4 h-4 mb-1" />
                )}
              </button>
            </div>
            <div data-validate="Repeat password">
              <input
                className="form-input"
                aria-label="rpassword"
                id="rpassword"
                type={isRpasswordVisible ? 'text' : 'password'}
                placeholder=""
                onChange={handleRpasswordChange}
                onBlur={handleRpasswordBlur}
                value={rpassword}
              />
            </div>
          </div>
        </div>
        {isAcademic && (
          <div className="form-check text-left">
            <input
              type="checkbox"
              className="form-check-input"
              name="academicLicenseCheckbox"
              checked={academicLicenseAccepted}
              onChange={handleAcademicLicenseCheckboxChange}
            />
            <label className="text-xs">
              I accept the{' '}
              <button
                type="button"
                onClick={() => window.open('/academic-license', '_blank', 'noopener,noreferrer')}
                className="text-sm font-semibold"
              >
                DISGENET academic License
              </button>
            </label>
          </div>
        )}
        <div className="form-check text-left">
          <input
            className="form-check-input"
            aria-label="privacyPolicyCheckbox"
            type="checkbox"
            name="privacyPolicyCheckbox"
            checked={privacyPolicyAccepted}
            onChange={handlePrivacyPolicyCheckboxChange}
          />
          <label htmlFor="privacy-policy" className="text-xs">
            I have read and accept{' '}
            <button
              type="button"
              onClick={() => window.open('/privacy-policy', '_blank', 'noopener,noreferrer')}
              className="text-sm font-semibold"
            >
              DISGENET&apos;s privacy policy
            </button>
          </label>
        </div>
        <hr></hr>
        <div>{!isRpasswordValid && <p className="apply-input-error">Please re-type your password</p>}</div>
        <div>{!isPasswordMatching && <p className="apply-input-error">Passwords must match</p>}</div>
        <div>{formErrorMessage && <p className="form-error">{formErrorMessage}</p>}</div>
        {(showResendForExistingUser || submissionStatusUnknown) && registeredEmail && (
          <ResendConfirmationEmailButton email={registeredEmail} className="mb-4" />
        )}
        <div className="div-apply-btn flex justify-center">
          <SharedButton
            variant="primary"
            size="md"
            type="button"
            onClick={handleOpenConfirmationPopup}
            disabled={isSubmitting}
          >
            {submitLabel}
          </SharedButton>
        </div>
      </form>
    </div>
  );

  const cardForm = (
    <div className="signup-form-card">
      <div className="signup-form-card-header">
        <h1 className="signup-form-hero-title">
          {heroTitle}
          <span className="home-hero-highlight">{heroTitleHighlight}</span>
          {heroTitleSuffix}
        </h1>
        <h2 className="signup-form-card-title">
          {isAcademic ? 'Submit your application' : 'Create your account'}
        </h2>
        <p className="signup-form-card-subtitle">
          {isAcademic
            ? 'Apply for your free academic license in under a minute.'
            : 'Start exploring DISGENET Advanced in under a minute.'}
        </p>
      </div>

      <form className="signup-form" onSubmit={handleSubmit} noValidate>
        <div className="signup-form-row">
          <SignupField label="Full name *" error={!isNameValid ? 'Please enter a valid name' : undefined}>
            <input
              className={cardInputClass(!isNameValid)}
              aria-label="name"
              id="name"
              type="text"
              autoComplete="name"
              placeholder="Ada Lovelace"
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              value={name}
            />
          </SignupField>
          <SignupField
            label="email *"
            error={
              !isEmailValid
                ? isAcademic
                  ? 'Please enter a valid institutional email'
                  : 'Only academic or business emails are accepted'
                : undefined
            }
          >
            <input
              className={cardInputClass(!isEmailValid)}
              aria-label="email"
              type="email"
              id="email"
              autoComplete="email"
              placeholder="you@lab.org"
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              value={email}
            />
          </SignupField>
        </div>

        {isChina && (
          <SignupField
            label="Phone number *"
            error={!isPhoneValid ? 'Please enter a valid phone number with no special characters' : undefined}
          >
            <input
              className={cardInputClass(!isPhoneValid)}
              aria-label="phone"
              type="text"
              placeholder="Phone number..."
              onChange={handlePhoneChange}
              onBlur={handlePhoneBlur}
              value={phone}
            />
            <p className="signup-form-china-note">
              Our official distributor, <a href="https://cloudscientific.com">Cloudscientific</a>, is here to
              assist you. Contact them at: +86 02154975000
            </p>
          </SignupField>
        )}

        <div className="signup-form-row">
          <SignupField
            label="Organization *"
            error={!isOrganizationValid ? "Please enter a valid organization's name" : undefined}
          >
            {isAcademic ? (
              <RorInstitutionSelect
                value={organization}
                onChange={handleOrganizationChange}
                onBlur={() => handleOrganizationBlur()}
                invalid={!isOrganizationValid}
                variant="signup"
                placeholder="Search university or research institute…"
              />
            ) : (
              <input
                className={cardInputClass(!isOrganizationValid)}
                aria-label="organization"
                type="text"
                autoComplete="organization"
                placeholder="e.g. Karolinska Institute"
                onChange={handleOrganizationChange}
                onBlur={handleOrganizationBlur}
                value={organization}
              />
            )}
          </SignupField>
          <SignupField label="Supporting link" hint="Optional">
            <input
              className={cardInputClass()}
              aria-label="supportingLink"
              type="url"
              placeholder="LinkedIn, ORCID, lab website…"
              onChange={handlesupportingLinksChange}
              value={linkedin}
            />
          </SignupField>
        </div>

        {!isAcademic && (
          <>
            <div className="signup-form-row">
              <SignupField label="Organization type *">
                <SignupSelect
                  aria-label="organizationType"
                  options={ORGANIZATION_TYPE_OPTIONS}
                  value={profile}
                  onChange={handleProfileChange}
                  placeholder="Choose one…"
                />
              </SignupField>
              <SignupField label="Purchase intent *">
                <SignupSelect
                  aria-label="purchaseIntent"
                  options={PURCHASE_INTENT_OPTIONS}
                  value={purchaseIntent}
                  onChange={handlePurchaseIntentChange}
                  placeholder="Choose one…"
                />
              </SignupField>
            </div>
          </>
        )}

        <SignupField label="What best describes your research context?">
          <MultiSelectDropdown
            options={[...RESEARCH_CONTEXT_OPTIONS]}
            value={researchContext}
            onChange={handleResearchContextChange}
            placeholder="Select your research context..."
            variant="signup"
            otherOptionValue="OTHER"
            otherValue={researchContextOther}
            onOtherChange={handleResearchContextOtherChange}
            onOtherBlur={handleResearchContextOtherBlur}
            otherError={
              researchContext.includes('OTHER') && !isResearchContextOtherValid
                ? 'Please specify your research context'
                : undefined
            }
            otherPlaceholder="Please specify..."
            otherRequired
            otherInputName="research-context-other"
          />
        </SignupField>

        {isAcademic && (
          <SignupField label="Field of study">
            <input
              className={cardInputClass()}
              aria-label="fieldOfStudy"
              type="text"
              placeholder="Bioinformatics, Genomics..."
              onChange={handlefieldOfStudyChange}
              value={fieldOfStudy}
            />
          </SignupField>
        )}

        <SignupField label="Password *" error={!isPasswordValid ? 'Please enter a password' : undefined}>
          <div className="signup-form-password-wrap">
            <input
              className={`${cardInputClass(!isPasswordValid)} signup-form-input--with-icon`}
              aria-label="password"
              id="password"
              type={isPasswordVisible ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="At least 10 characters"
              onChange={handlePasswordChange}
              onBlur={handlePasswordBlur}
              value={password}
            />
            <button
              type="button"
              className="signup-form-password-toggle"
              onClick={() => setIsPasswordVisible(!isPasswordVisible)}
              aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
            >
              {isPasswordVisible ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
            </button>
          </div>
          {password && (
            <div className="signup-form-password-strength">
              <div className="signup-form-password-bars">
                {[0, 1, 2, 3].map(index => (
                  <span
                    key={index}
                    className={`signup-form-password-bar${
                      index < passwordScore
                        ? passwordScore >= 3
                          ? ' signup-form-password-bar--strong'
                          : ' signup-form-password-bar--active'
                        : ''
                    }`}
                  />
                ))}
              </div>
              <span className="signup-form-password-label">{passwordScoreLabel}</span>
            </div>
          )}
        </SignupField>

        <SignupField
          label="Retype Password *"
          error={
            !isRpasswordValid
              ? 'Please re-type your password'
              : !isPasswordMatching
                ? 'Passwords must match'
                : undefined
          }
        >
          <div className="signup-form-password-wrap">
            <input
              className={`${cardInputClass()} signup-form-input--with-icon`}
              aria-label="rpassword"
              id="rpassword"
              type={isRpasswordVisible ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Retype your password"
              onChange={handleRpasswordChange}
              onBlur={handleRpasswordBlur}
              value={rpassword}
            />
            <button
              type="button"
              className="signup-form-password-toggle"
              onClick={() => setIsRpasswordVisible(!isRpasswordVisible)}
              aria-label={isRpasswordVisible ? 'Hide password' : 'Show password'}
            >
              {isRpasswordVisible ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
            </button>
          </div>
        </SignupField>

        {isAcademic && (
          <label className="signup-form-checkbox">
            <input
              type="checkbox"
              name="academicLicenseCheckbox"
              checked={academicLicenseAccepted}
              onChange={handleAcademicLicenseCheckboxChange}
            />
            <span>
              I accept the{' '}
              <button
                type="button"
                onClick={() => window.open('/academic-license', '_blank', 'noopener,noreferrer')}
              >
                DISGENET academic License
              </button>
            </span>
          </label>
        )}

        <label className="signup-form-checkbox">
          <input
            aria-label="privacyPolicyCheckbox"
            type="checkbox"
            name="privacyPolicyCheckbox"
            checked={privacyPolicyAccepted}
            onChange={handlePrivacyPolicyCheckboxChange}
          />
          <span>
            I have read and accept the{' '}
            <button
              type="button"
              onClick={() => window.open('/privacy-policy', '_blank', 'noopener,noreferrer')}
            >
              DISGENET privacy policy
            </button>{' '}
            and terms of service.
          </span>
        </label>

        {formErrorMessage && (
          <p className="signup-form-message signup-form-message--error">{formErrorMessage}</p>
        )}
        {(showResendForExistingUser || submissionStatusUnknown) && registeredEmail && (
          <ResendConfirmationEmailButton email={registeredEmail} className="mb-4" />
        )}
        <button
          type="button"
          className="signup-form-submit"
          onClick={handleOpenConfirmationPopup}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <ArrowPathIcon className="signup-form-submit-icon signup-form-submit-icon--spin" aria-hidden />
              Creating account…
            </>
          ) : (
            <>
              {submitLabel}
              <ArrowRightIcon className="signup-form-submit-icon" aria-hidden />
            </>
          )}
        </button>

        <p className="signup-form-signin">
          Already have an account?{' '}
          <Link to="/login" className="signup-form-signin-link">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );

  return (
    <>
      {isCardVariant ? cardForm : legacyForm}
      {showConfirmationPopup && (
        <ConfirmationPopup
          onClose={() => setShowConfirmationPopup(false)}
          onConfirm={handleSubmit}
          isAcademic={isAcademic}
          isSubmitting={isSubmitting}
        />
      )}
      {showVerifyEmailPopup && registeredEmail && (
        <VerifyEmailPopup email={registeredEmail} onClose={() => setShowVerifyEmailPopup(false)} />
      )}

      {/* LinkedIn Insight Tag noscript fallback */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          alt=""
          src="https://px.ads.linkedin.com/collect/?pid=8934697&fmt=gif"
        />
      </noscript>
    </>
  );
}

export default SignUpForm;
